import { c as _c } from "react/compiler-runtime";
import { cn } from "@/utils/helpers";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { ButtonHTMLAttributes, ReactNode } from "react";
const ICON_SIZES = {
  xs: 12,
  sm: 16,
  md: 16,
  lg: 20
};
const applyDynamicClasses = (isPrimary: boolean, glowIndex: 1 | 2): {
  base: string;
  color: string;
} => {
  const baseClasses = "absolute transition-[transform,opacity] duration-300 ease-out w-56 h-48 blur-[1.25rem] pointer-events-none";
  const colorClass = isPrimary ? `button-glow-primary-${glowIndex}` : `button-glow-secondary-${glowIndex}`;
  return {
    base: baseClasses,
    color: colorClass
  };
};
const buttonVariants = cva("rounded-3xl inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold cursor-pointer transition-[transform,opacity,background-color,border-color,box-shadow] duration-300 ease-out relative z-10 disabled:opacity-50 disabled:cursor-default overflow-hidden group", {
  variants: {
    variant: {
      primary: "glass border-none text-(--color-accent-2) hover:opacity-90 active:opacity-80",
      secondary: "backdrop-blur-lg border border-white/25 bg-gradient-to-tr from-black/35 to-black/15 shadow-lg hover:border-white/45 hover:bg-gradient-to-tr hover:from-white/25 hover:to-black/25 text-(--color-foreground)",
      ghost: "bg-transparent border-none text-(--color-foreground) hover:opacity-80 active:opacity-60 shadow-none"
    },
    size: {
      xs: "h-6 px-3 text-xs",
      sm: "h-8 px-4 text-sm",
      md: "h-10 px-5 text-sm",
      lg: "h-12 px-6 text-base"
    }
  },
  defaultVariants: {
    variant: "primary",
    size: "lg"
  }
});
type ButtonIconPosition = "left" | "right";
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  children?: ReactNode;
  icon?: ReactNode;
  iconPosition?: ButtonIconPosition;
  iconRotate?: number;
  isIcon?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  "aria-label"?: string;
  "data-testid"?: string;
}
export { buttonVariants };
export const Button = t0 => {
  const $ = _c(75);
  let ariaLabel;
  let children;
  let className;
  let dataTestId;
  let icon;
  let isIcon;
  let isLoading;
  let onClick;
  let props;
  let size;
  let t1;
  let t2;
  let t3;
  let variant;
  if ($[0] !== t0) {
    ({
      children,
      onClick,
      className,
      type: t1,
      variant,
      size,
      icon,
      iconPosition: t2,
      isIcon,
      isLoading,
      isDisabled: t3,
      "aria-label": ariaLabel,
      "data-testid": dataTestId,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = ariaLabel;
    $[2] = children;
    $[3] = className;
    $[4] = dataTestId;
    $[5] = icon;
    $[6] = isIcon;
    $[7] = isLoading;
    $[8] = onClick;
    $[9] = props;
    $[10] = size;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = variant;
  } else {
    ariaLabel = $[1];
    children = $[2];
    className = $[3];
    dataTestId = $[4];
    icon = $[5];
    isIcon = $[6];
    isLoading = $[7];
    onClick = $[8];
    props = $[9];
    size = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    variant = $[14];
  }
  const type = t1 === undefined ? "button" : t1;
  const iconPosition = t2 === undefined ? "left" : t2;
  const isDisabled = t3 === undefined ? false : t3;
  const iconSize = size ? ICON_SIZES[size] : ICON_SIZES.lg;
  let t4;
  if ($[15] !== icon || $[16] !== iconSize || $[17] !== isLoading) {
    t4 = () => {
      if (isLoading) {
        return <LoaderCircle width={iconSize} height={iconSize} className="shrink-0 animate-spin" aria-label="Loading icon" />;
      }
      if (icon) {
        return <>{icon}</>;
      }
    };
    $[15] = icon;
    $[16] = iconSize;
    $[17] = isLoading;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const renderIcon = t4;
  const isPrimary = variant === "primary" || variant === undefined;
  const isGhost = variant === "ghost";
  let t10;
  let t11;
  let t12;
  let t13;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[19] !== ariaLabel || $[20] !== className || $[21] !== dataTestId || $[22] !== isDisabled || $[23] !== isGhost || $[24] !== isIcon || $[25] !== isLoading || $[26] !== isPrimary || $[27] !== onClick || $[28] !== props || $[29] !== size || $[30] !== type || $[31] !== variant) {
    const firstGlowClasses = applyDynamicClasses(isPrimary, 1);
    const secondGlowClasses = applyDynamicClasses(isPrimary, 2);
    t5 = type;
    t6 = onClick;
    t7 = isDisabled || isLoading;
    t8 = ariaLabel;
    t9 = dataTestId;
    if ($[41] !== className || $[42] !== isIcon || $[43] !== size || $[44] !== variant) {
      t10 = cn(buttonVariants({
        variant,
        size
      }), isIcon && "w-12! h-12! rounded-full! p-0!", className);
      $[41] = className;
      $[42] = isIcon;
      $[43] = size;
      $[44] = variant;
      $[45] = t10;
    } else {
      t10 = $[45];
    }
    t11 = props;
    if ($[46] !== isGhost || $[47] !== isPrimary) {
      t12 = !isGhost && <div className={cn("absolute transition-transform ease-out", isPrimary ? "z-10 -translate-x-44 group-hover:translate-x-120 duration-500 h-full w-44 bg-linear-to-r from-gray-500 to-white/10 opacity-30 -skew-x-12 ease-in" : "inset-0 bg-linear-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full duration-700")} />;
      $[46] = isGhost;
      $[47] = isPrimary;
      $[48] = t12;
    } else {
      t12 = $[48];
    }
    t13 = isPrimary && <><div className={cn(firstGlowClasses.base, "group-hover:-left-1.25 group-hover:-top-42.5 -left-37.5 -top-37.5", firstGlowClasses.color)} /><div className={cn(secondGlowClasses.base, "group-hover:-right-1.25 group-hover:-bottom-42.5 -right-37.5 -bottom-37.5", secondGlowClasses.color)} /></>;
    $[19] = ariaLabel;
    $[20] = className;
    $[21] = dataTestId;
    $[22] = isDisabled;
    $[23] = isGhost;
    $[24] = isIcon;
    $[25] = isLoading;
    $[26] = isPrimary;
    $[27] = onClick;
    $[28] = props;
    $[29] = size;
    $[30] = type;
    $[31] = variant;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
  } else {
    t10 = $[32];
    t11 = $[33];
    t12 = $[34];
    t13 = $[35];
    t5 = $[36];
    t6 = $[37];
    t7 = $[38];
    t8 = $[39];
    t9 = $[40];
  }
  const t14 = isGhost ? "flex flex-col items-center justify-center gap-2" : "inline-flex items-center justify-center gap-2";
  let t15;
  if ($[49] !== t14) {
    t15 = cn("relative z-10 text-inherit", t14);
    $[49] = t14;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== iconPosition || $[52] !== renderIcon) {
    t16 = iconPosition === "left" && renderIcon();
    $[51] = iconPosition;
    $[52] = renderIcon;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  const t17 = !isIcon && (children || "Button");
  let t18;
  if ($[54] !== iconPosition || $[55] !== renderIcon) {
    t18 = iconPosition === "right" && renderIcon();
    $[54] = iconPosition;
    $[55] = renderIcon;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== t15 || $[58] !== t16 || $[59] !== t17 || $[60] !== t18) {
    t19 = <span className={t15}>{t16}{t17}{t18}</span>;
    $[57] = t15;
    $[58] = t16;
    $[59] = t17;
    $[60] = t18;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== t10 || $[63] !== t11 || $[64] !== t12 || $[65] !== t13 || $[66] !== t19 || $[67] !== t5 || $[68] !== t6 || $[69] !== t7 || $[70] !== t8 || $[71] !== t9) {
    t20 = <button type={t5} onClick={t6} disabled={t7} aria-label={t8} data-testid={t9} className={t10} {...t11}>{t12}{t13}{t19}</button>;
    $[62] = t10;
    $[63] = t11;
    $[64] = t12;
    $[65] = t13;
    $[66] = t19;
    $[67] = t5;
    $[68] = t6;
    $[69] = t7;
    $[70] = t8;
    $[71] = t9;
    $[72] = t20;
  } else {
    t20 = $[72];
  }
  const buttonElement = t20;
  if (isGhost) {
    return buttonElement;
  }
  let t21;
  if ($[73] !== buttonElement) {
    t21 = <div className="relative group">{buttonElement}</div>;
    $[73] = buttonElement;
    $[74] = t21;
  } else {
    t21 = $[74];
  }
  return t21;
};